<template>
  <div class="container my-5">
    <button @click="goBack" class="btn btn-secondary mb-3">
      <font-awesome-icon icon="arrow-left" /> Zurück
    </button>
    <button @click="goToOverview" class="btn btn-info mb-3" style="margin-left: 10px;">
      <font-awesome-icon icon="list" /> Auszahlungen anzeigen
    </button>

    <h2 class="text-center mb-4">Auszahlung</h2>

    <div v-if="errorMessage" class="alert alert-warning">
      {{ errorMessage }}
    </div>

    <div class="search-container mb-4 position-relative">
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        class="form-control"
        placeholder="Artikelnummer, Kundennummer oder Name eingeben..."
        autocomplete="off"
        @input="onSearchInput"
        @focus="searchQuery && (showDropdown = true)"
        @blur="onSearchBlur"
        @keydown.arrow-down="onArrowDown"
        @keydown.arrow-up="onArrowUp"
        @keydown.enter="onEnter"
      />
      <ul v-if="showDropdown && searchSuggestions.length" class="dropdown-list list-unstyled">
        <li
          v-for="(c, i) in searchSuggestions"
          :key="c.id"
          :class="{ 'active': i === dropdownIndex }"
          @mousedown.prevent="selectCustomer(c)"
        >
          {{ c.FirstName }} {{ c.LastName }} (Nr. {{ c.id }}) – {{ c.articleCount }} Artikel
        </li>
      </ul>
      <ul v-else-if="showDropdown && searchQuery.length >= 1 && !searchLoading" class="dropdown-list list-unstyled">
        <li class="text-muted">Keine Lieferanten gefunden</li>
      </ul>
    </div>

    <div v-if="articles.length">
      <div v-if="customerName" class="alert alert-info d-flex justify-content-between align-items-center flex-wrap gap-2">
        <strong>Lieferant/Kunde: {{ customerName }} (Nr. {{ customerId }})</strong>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="setAllSelected(true)">
            Alle auswählen
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="setAllSelected(false)">
            Keine auswählen
          </button>
        </div>
      </div>
      <table class="table table-bordered avoid-page-break">
        <thead>
          <tr>
            <th style="width: 5%;">Auswahl</th>
            <th style="width: 10%;">Artikel-Nr.</th>
            <th style="width: 35%;">Artikel</th>
            <th style="width: 12%;">Angenommen</th>
            <th style="width: 12%;">Verkauft</th>
            <th style="width: 13%;">Verkaufspreis</th>
            <th style="width: 13%;">Kommission</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="article in articles" :key="article.ArticleId">
            <td>
              <input type="checkbox" v-model="article.selected" />
            </td>
            <td>{{ article.ArticleId }}</td>
            <td>{{ article.Name }}<span v-if="article.Color">, {{ article.Color }}</span></td>
            <td>{{ formatDate(article.ReceivedDate) }}</td>
            <td>{{ formatDate(article.SaleDate) }}</td>
            <td>{{ $formatPrice(article.PriceDiscountLevel3 ?? article.PriceDiscountLevel2 ?? article.PriceDiscountLevel1 ?? article.SalePrice) }}</td>
            <td>{{ $formatPrice(article.CommissionAmount) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="6" class="text-right">
              <strong>Total Kommission:</strong>
            </td>
            <td>
              <strong>{{ $formatPrice(totalAmount) }}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
      <button @click="processPayout" class="btn btn-success">
        Auszahlen
      </button>
    </div>
    <div v-else>
      <p>Keine Artikel gefunden. Bitte suche einen Kunden.</p>
    </div>

    <!-- Versteckter PDF-Teil -->
    <div ref="pdfContent" style="display: none;">
      <!-- … dein existierendes PDF-Template … -->
    </div>
  </div>
</template>


<script>
import axios from '@/services/axios';
import html2pdf from 'html2pdf.js';

export default {
  name: 'Auszahlung',
  data() {
    return {
      searchQuery: '',
      articles: [],
      customerName: '',
      customerId: null,
      errorMessage: '',
      searchSuggestions: [],
      searchLoading: false,
      showDropdown: false,
      dropdownIndex: -1,
      searchDebounce: null,
    };
  },
  computed: {
    totalAmount() {
      return this.articles.reduce((sum, article) => {
        if (article.selected) {
          return sum + parseFloat(article.CommissionAmount || 0);
        }
        return sum;
      }, 0);
    },
    selectedArticlesForPdf() {
      return this.articles.filter(article => article.selected);
    },
  },
  methods: {
    goBack() {
      this.$router.push('/admin');
    },
    goToOverview() {
      this.$router.push('/admin/payouts-overview');
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      const day = ('0' + d.getDate()).slice(-2);
      const month = ('0' + (d.getMonth() + 1)).slice(-2);
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    },
    setAllSelected(selected) {
      this.articles.forEach(a => { a.selected = selected; });
    },
    onSearchInput() {
      this.errorMessage = '';
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      const q = this.searchQuery.trim();
      if (!q) {
        this.searchSuggestions = [];
        this.showDropdown = false;
        return;
      }
      this.searchDebounce = setTimeout(async () => {
        this.searchLoading = true;
        this.showDropdown = true;
        this.dropdownIndex = -1;
        try {
          const res = await axios.get('/payouts/unpaid/search', { params: { q } });
          this.searchSuggestions = res.data;
        } catch (err) {
          this.searchSuggestions = [];
          this.errorMessage = 'Fehler bei der Suche.';
        }
        this.searchLoading = false;
      }, 200);
    },
    onSearchBlur() {
      setTimeout(() => { this.showDropdown = false; }, 150);
    },
    onArrowDown(e) {
      if (!this.showDropdown || !this.searchSuggestions.length) return;
      e.preventDefault();
      this.dropdownIndex = Math.min(this.dropdownIndex + 1, this.searchSuggestions.length - 1);
    },
    onArrowUp(e) {
      if (!this.showDropdown || !this.searchSuggestions.length) return;
      e.preventDefault();
      this.dropdownIndex = Math.max(this.dropdownIndex - 1, -1);
    },
    onEnter(e) {
      if (this.showDropdown && this.searchSuggestions.length) {
        e.preventDefault();
        const c = this.dropdownIndex >= 0 ? this.searchSuggestions[this.dropdownIndex] : this.searchSuggestions[0];
        this.selectCustomer(c);
      }
    },
    async selectCustomer(c) {
      this.customerName = `${c.FirstName} ${c.LastName}`;
      this.customerId = c.id;
      this.searchQuery = `${c.FirstName} ${c.LastName} (Nr. ${c.id})`;
      this.showDropdown = false;
      this.searchSuggestions = [];
      await this.loadArticlesForCustomer(c.id);
    },
    async loadArticlesForCustomer(customerId) {
      this.errorMessage = '';
      try {
        const res = await axios.get('/payouts/unpaid', { params: { Customerid: customerId } });
        this.articles = res.data.map(a => ({ ...a, selected: true }));
      } catch (err) {
        console.error(err);
        this.errorMessage = 'Fehler beim Laden der Artikel.';
        this.articles = [];
      }
    },
    async processPayout() {
      const selectedArticleIds = this.articles
        .filter(article => article.selected)
        .map(article => article.ArticleId);

      if (selectedArticleIds.length === 0) {
        alert('Bitte wählen Sie mindestens einen Artikel aus.');
        return;
      }

      try {
        const payload = {
          articleIds: selectedArticleIds,
          customerId: this.customerId,
        };
        await axios.post('/payouts', payload);
        this.generatePdf();
        alert('Auszahlung erfolgreich durchgeführt.');
        if (this.customerId) this.loadArticlesForCustomer(this.customerId);
      } catch (error) {
        console.error(error);
        alert('Fehler beim Durchführen der Auszahlung.');
      }
    },
    generatePdf() {
      const pdfElement = this.$refs.pdfContent;
      pdfElement.style.display = 'block';

      this.$nextTick(() => {
        const options = {
          margin: 0.5,
          filename: `Auszahlungsbeleg_${this.customerName}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'A4', orientation: 'portrait' },
          pagebreak: { mode: 'avoid-all' },
        };

        const worker = html2pdf().set(options).from(pdfElement);
        worker
          .toPdf()
          .get('pdf')
          .then(pdf => {
            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();
              const text = `Seite ${i} von ${totalPages}`;
              const textWidth = pdf.getTextWidth(text);
              const x = (pageWidth - textWidth) / 2;
              const y = pageHeight - 0.3;
              pdf.text(text, x, y);
            }
          })
          .then(() => worker.save())
          .then(() => {
            pdfElement.style.display = 'none';
          });
      });
    },
  },
};
</script>


<style>
/* Kein "scoped", damit @media print-Regeln für html2pdf wirksam sind */
.container {
  margin-top: 20px;
}

.search-container {
  max-width: 500px;
}

.search-container input {
  width: 100%;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  margin-top: 2px;
  z-index: 1000;
  padding: 0;
}

.dropdown-list li {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.dropdown-list li:last-child {
  border-bottom: none;
}

.dropdown-list li:hover,
.dropdown-list li.active {
  background: #e7f1ff;
}

.table {
  margin-bottom: 20px;
}

/* PDF Template Styling */
.pdf-template {
  font-family: 'Marcellus', Arial, sans-serif;
  padding-bottom: 1in;
}

.header {
  display: flex;
  margin-bottom: 20px;
  position: relative;
}

.address-block {
  position: absolute;
  top: 30px;
  right: 0;
  width: 200px;
  text-align: left;
}

.address-block p {
  margin: 0;
  line-height: 1.2;
}

/* Verhindert das Auseinanderreißen bestimmter Bereiche beim Drucken/Umwandeln */
.avoid-page-break {
  page-break-inside: avoid;
}

/* 
    Möchtest du bestimmte Elemente immer auf einer neuen Seite beginnen lassen,
    kannst du zusätzlich .page-break verwenden:
    .page-break { page-break-before: always; }
  */

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

th,
td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

th {
  background-color: #f2f2f2;
}

.footer {
  text-align: center;
  margin-top: 20px;
}

.signature-section {
  margin-top: 50px;
}

/* 
    Diese Regeln greifen in erster Linie bei reinem "print" (z. B. im Browser).
    html2pdf nutzt sie nur teilweise, je nach Version und Rendering.
  */
@media print {
  .avoid-page-break {
    page-break-inside: avoid !important;
  }

  .page-break {
    page-break-before: always;
  }
}
</style>